import { useEffect, useState, type FormEvent } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { Modal } from '@/components/shared/Modal';
import {
  PLATFORM_ROLES,
  type PlatformRole,
  type PlatformUserSummary,
} from '@/types/platform-user.types';
import { RoleBadge, roleLabel } from './user-presentation';

/**
 * Phase 12.6-5 — confirmation for the three lifecycle actions.
 *
 * Design of record: `PLATFORM_USERS_DESIGN.md` v1.0 §6, §8.2–§8.4.
 *
 * ── Friction tracks consequence, not danger-theatre ─────────────────────────
 * §6 requires a confirmation for **Change role**, **Deactivate** and **Revoke
 * sessions**, and explicitly **none** for Activate — "Sign-in restored. Does not
 * revoke sessions (there are none)." Ceremony where nothing is at stake only
 * teaches people to click through ceremony where something is. Activate is
 * therefore included here as a plain confirm with no warning styling, matching
 * the Companies precedent for its benign transition.
 *
 * ── Typed state survives a rejected submit ──────────────────────────────────
 * `submitting` and `error` are PROPS, owned by the page. The dialog stays
 * mounted through a failed mutation, so a selected target role is still there
 * after a 403 or a 409. A refetch-driven skeleton must never unmount this — the
 * defect the Subscriptions work caught, and the reason the parent refetches only
 * AFTER the dialog closes.
 */

export type UserAction = 'role' | 'deactivate' | 'activate' | 'revoke-sessions';

interface ActionCopy {
  title: string;
  /** Stated plainly, including the session consequence where there is one. */
  body: string;
  confirmLabel: string;
  /** Whether this action terminates the target's sessions. */
  revokesSessions: boolean;
  destructive: boolean;
}

export const USER_ACTION_COPY: Record<UserAction, ActionCopy> = {
  role: {
    title: 'Change platform role',
    body: 'Changing a role takes effect immediately: the account’s current sessions are ended and they must sign in again to pick up the new authority.',
    confirmLabel: 'Change role',
    revokesSessions: true,
    destructive: false,
  },
  deactivate: {
    title: 'Deactivate account',
    body: 'The account will not be able to sign in. Existing sessions are ended immediately. Nothing is deleted, and the account can be reactivated at any time.',
    confirmLabel: 'Deactivate',
    revokesSessions: true,
    destructive: true,
  },
  activate: {
    title: 'Activate account',
    body: 'Sign-in is restored for this account. No sessions are revoked — a deactivated account has none.',
    confirmLabel: 'Activate',
    revokesSessions: false,
    destructive: false,
  },
  'revoke-sessions': {
    title: 'Revoke all sessions',
    body: 'Every access and refresh token for this account is invalidated. They stay active and keep their role — they simply have to sign in again.',
    confirmLabel: 'Revoke sessions',
    revokesSessions: true,
    destructive: true,
  },
};

interface Props {
  action: UserAction | null;
  user: PlatformUserSummary;
  /** True when the target is the signed-in operator. Only reachable for revoke-sessions. */
  isSelf: boolean;
  submitting: boolean;
  error: string;
  onClose: () => void;
  /** `role` is supplied only for the role action. */
  onConfirm: (role?: PlatformRole) => void;
}

export function PlatformUserActionDialog({
  action,
  user,
  isSelf,
  submitting,
  error,
  onClose,
  onConfirm,
}: Props) {
  const [targetRole, setTargetRole] = useState<PlatformRole | ''>('');

  // Every open starts clean — a role chosen for one user must never be carried
  // into a dialog opened for another.
  useEffect(() => {
    if (action) setTargetRole('');
  }, [action, user.id]);

  if (!action) return null;

  const copy = USER_ACTION_COPY[action];
  const roleUnchanged = action === 'role' && targetRole === user.role;
  const canSubmit = !submitting && (action !== 'role' || (targetRole !== '' && !roleUnchanged));

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    if (!canSubmit) return;
    onConfirm(action === 'role' ? (targetRole as PlatformRole) : undefined);
  };

  return (
    <Modal open onClose={submitting ? () => undefined : onClose} title={copy.title} size="md">
      <form onSubmit={submit} className="space-y-4 px-6 py-5">
        <p className="text-sm text-muted-foreground">{copy.body}</p>

        {/* Who this affects — never assumed from context. */}
        <div className="rounded-lg border border-border bg-muted/30 px-3 py-2">
          <p className="text-xs text-muted-foreground">Account</p>
          <p className="break-words text-sm font-medium text-foreground">{user.displayName}</p>
          <p className="break-all text-xs text-muted-foreground">{user.email}</p>
        </div>

        {action === 'role' && (
          <>
            {/* §6 — "clearly display current role and target role". */}
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="text-muted-foreground">Current role:</span>
              <RoleBadge role={user.role} />
            </div>

            <div>
              <label
                htmlFor="platform-user-target-role"
                className="mb-1.5 block text-sm font-medium text-foreground"
              >
                New role <span className="text-destructive">*</span>
              </label>
              <select
                id="platform-user-target-role"
                value={targetRole}
                onChange={(e) => setTargetRole(e.target.value as PlatformRole)}
                className="h-11 w-full rounded-lg border border-border bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring sm:h-10"
              >
                <option value="">Select a role…</option>
                {/* The seven frozen D-041 roles. No other value can be chosen. */}
                {PLATFORM_ROLES.map((r) => (
                  <option key={r} value={r}>
                    {roleLabel(r)}
                  </option>
                ))}
              </select>
              {roleUnchanged && (
                <p className="mt-1.5 text-xs text-muted-foreground">
                  That is already this account’s role — nothing would change.
                </p>
              )}
            </div>
          </>
        )}

        {/* The self case is only reachable for revoke-sessions: role and status
            changes against yourself are refused by the server and are not
            offered in the UI at all. */}
        {isSelf && action === 'revoke-sessions' && (
          <div
            role="note"
            className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2"
          >
            <FontAwesomeIcon
              icon={faTriangleExclamation}
              className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400"
            />
            <p className="text-sm text-amber-700 dark:text-amber-400">
              This is your own account — <strong>you will be signed out immediately</strong> and
              will need to sign in again.
            </p>
          </div>
        )}

        {error && (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2"
          >
            <FontAwesomeIcon
              icon={faTriangleExclamation}
              className="mt-0.5 h-3.5 w-3.5 shrink-0 text-destructive"
            />
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        {/* Full-width, stacked below sm — Modal Standards. */}
        <div className="flex flex-col-reverse gap-2 pt-1 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="min-h-11 rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted disabled:opacity-50 sm:min-h-0"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            className={`min-h-11 rounded-lg px-4 py-2 text-sm font-semibold text-white transition-colors disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0 ${
              copy.destructive
                ? 'bg-destructive hover:bg-destructive/90'
                : 'bg-primary hover:bg-primary/90'
            }`}
          >
            {submitting && <FontAwesomeIcon icon={faSpinner} spin className="mr-2 h-3.5 w-3.5" />}
            {copy.confirmLabel}
          </button>
        </div>
      </form>
    </Modal>
  );
}
